import React from 'react';
import { reportFrontendError } from '../utils/errorBeacon';

/**
 * ErrorBoundary — stops a single page crash from white-screening the app.
 *
 * We use this around public routes (e.g. /book/:slug, /auth/magic/:token) so
 * a bad prop or unparsed backend field never leaves visitors looking at a
 * blank page. The UI is intentionally lightweight; a proper support link is
 * surfaced so users can reach us if they hit it.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
    reportFrontendError({
      context: 'react_error_boundary',
      message: error?.message || String(error),
      detail: (info?.componentStack || error?.stack || '').slice(0, 4000),
    });
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Iter-200 — optional inline render fallback for cases where the
    // boundary wraps a modal/drawer and a full-screen crash card would
    // be visually wrong (e.g. ApplicantDrawer on AdminShiftDetail).
    // Callers pass `renderFallback(reset, error)` to opt in.
    if (typeof this.props.renderFallback === 'function') {
      try {
        return this.props.renderFallback(this.reset, this.state.error);
      } catch (_) {
        // fall through to default UI
      }
    }

    // Iter-196 — optional caller-supplied fallback link so the talent
    // gig/apply flow can recover into "Browse gigs" instead of leaving
    // users stranded on a generic refresh page.
    const fallbackHref = this.props.fallbackHref || '/';
    const fallbackLabel = this.props.fallbackLabel || 'Go Home';

    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-white"
        data-testid="error-boundary-fallback"
      >
        <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
        <p className="text-sm text-gray-600 mt-2 max-w-md">
          We hit an unexpected error rendering this page. Our team has been
          notified. You can refresh or head back.
        </p>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded bg-cyan-500 hover:bg-cyan-600 text-white text-sm"
            data-testid="error-boundary-reload"
          >
            Refresh
          </button>
          <a
            href={fallbackHref}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            data-testid="error-boundary-home"
          >
            {fallbackLabel}
          </a>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
